import { useImageVariant } from '~/features/assets/imageWatermarkContext';
import type { ImageGenerationHandle } from 'img-fx';
import {
  type ImgHTMLAttributes,
  type ReactElement,
  forwardRef,
  useEffect,
  useRef,
  useState,
} from 'react';

interface AppImageProps extends ImgHTMLAttributes<HTMLImageElement> {
  assetId?: string;
  enableEffect?: boolean;
  wrapperClassName?: string;
}

let webGlSupported: boolean | undefined;
type ImageGenerationComponent = (typeof import('img-fx'))['ImageGeneration'];
let imageGenerationPromise: Promise<ImageGenerationComponent> | undefined;

const loadImageGeneration = (): Promise<ImageGenerationComponent> => {
  imageGenerationPromise ??= import('img-fx').then(
    module => module.ImageGeneration
  );
  return imageGenerationPromise;
};

const supportsOrganicEffect = (): boolean => {
  if (webGlSupported !== undefined) return webGlSupported;
  if (
    typeof window === 'undefined' ||
    typeof ResizeObserver === 'undefined' ||
    typeof IntersectionObserver === 'undefined'
  ) {
    return false;
  }

  try {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('webgl2') ?? canvas.getContext('webgl');
    webGlSupported = Boolean(context);
    context?.getExtension('WEBGL_lose_context')?.loseContext();
  } catch {
    webGlSupported = false;
  }

  return webGlSupported;
};

const readCssContentUrl = (image: HTMLImageElement): string | undefined => {
  const content = window.getComputedStyle(image).content;
  const match = content.match(/^url\(["']?(.*?)["']?\)$/);
  return match?.[1];
};

export const AppImage = forwardRef<HTMLImageElement, AppImageProps>(
  (
    {
      enableEffect = true,
      wrapperClassName,
      src: fallbackSrc,
      assetId,
      onLoad,
      ...props
    },
    ref
  ): ReactElement => {
    const src = useImageVariant(fallbackSrc, assetId);
    const effectRef = useRef<ImageGenerationHandle>(null);
    const imageRef = useRef<HTMLImageElement | null>(null);
    const initialEffectSrcRef = useRef<string | undefined>(undefined);
    const [ImageEffect, setImageEffect] =
      useState<ImageGenerationComponent | null>(null);
    const [effectSrc, setEffectSrc] = useState<string | undefined>(undefined);
    const [effectFinished, setEffectFinished] = useState(false);
    const effectEnabled = ImageEffect !== null;

    const setImageRef = (image: HTMLImageElement | null): void => {
      imageRef.current = image;
      if (typeof ref === 'function') ref(image);
      else if (ref) ref.current = image;
    };

    useEffect(() => {
      const reducedMotion = window.matchMedia?.(
        '(prefers-reduced-motion: reduce)'
      );
      let cancelled = false;
      const update = (): void => {
        if (
          !enableEffect ||
          reducedMotion?.matches ||
          !supportsOrganicEffect()
        ) {
          setImageEffect(null);
          return;
        }
        void loadImageGeneration().then(component => {
          if (!cancelled) setImageEffect(() => component);
        });
      };

      update();
      reducedMotion?.addEventListener('change', update);
      return () => {
        cancelled = true;
        reducedMotion?.removeEventListener('change', update);
      };
    }, [enableEffect]);

    useEffect(() => {
      if (!effectEnabled || !imageRef.current) return undefined;

      const updateSource = (): void => {
        const renderedSrc = readCssContentUrl(imageRef.current!);
        if (renderedSrc) setEffectSrc(renderedSrc);
      };
      const observer = new MutationObserver(updateSource);
      observer.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['class', 'data-theme', 'style'],
      });
      updateSource();
      return () => observer.disconnect();
    }, [effectEnabled, src]);

    useEffect(() => {
      if (!effectEnabled || !effectSrc || !effectRef.current) return;
      if (initialEffectSrcRef.current) return;
      initialEffectSrcRef.current = src;
      effectRef.current.triggerReveal({ hold: 'manual' });
    }, [effectEnabled, effectSrc, src]);

    useEffect(() => {
      if (initialEffectSrcRef.current && src !== initialEffectSrcRef.current) {
        setEffectFinished(true);
      }
    }, [src]);

    const image = (
      <img
        {...props}
        onLoad={event => {
          setEffectSrc(
            readCssContentUrl(event.currentTarget) ||
              event.currentTarget.currentSrc ||
              src
          );
          onLoad?.(event);
        }}
        ref={setImageRef}
        src={src}
      />
    );

    if (!ImageEffect || !src || effectFinished) return image;

    return (
      <ImageEffect
        autoReveal={false}
        className={wrapperClassName}
        images={effectSrc ?? src}
        onCycle={({ phase }) => {
          if (phase === 'visible') setEffectFinished(true);
        }}
        preset="pixels-organic"
        ref={effectRef}
      >
        {image}
      </ImageEffect>
    );
  }
);

AppImage.displayName = 'AppImage';
